import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Layout,
  Typography,
  Space,
  Card,
  Modal,
  Descriptions,
  message,
  Alert
} from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  CloseOutlined
} from '@ant-design/icons';
import TaskList from '../../components/Tasks/TaskList';
import TaskForm from '../../components/Tasks/TaskForm';
import { getTaskById, deleteTask, updateTaskStatus } from '../../services/taskApi';
import { useProjects } from '../../hooks/useProjects'
import { useAuth } from '../../contexts/AuthContext'

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const TasksPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null)

  // ใช้ useProjects hook และ getProjectById เพื่อดึงข้อมูลโปรเจ็คตาม ID
  const projectsHook = useProjects({ autoFetch: false })
  const { getProjectById } = projectsHook
  const [projectData, setProjectData] = useState(null)

  // ดึงข้อมูลโปรเจ็คตาม ID เมื่อ projectId เปลี่ยน
  React.useEffect(() => {
    let isMounted = true // Track component mount state

    const fetchProjectData = async () => {
      console.log('🔍 ProjectTasksPage - Starting fetch, projectId:', projectId)
      console.log('🔍 ProjectTasksPage - getProjectById available:', !!getProjectById)

      if (!projectId) {
        console.log('❌ No projectId provided')
        if (isMounted) {
          setProjectData(null)
          setLoading(false)
        }
        return
      }

      if (!getProjectById) {
        console.log('❌ getProjectById not available yet')
        return
      }

      try {
        console.log('📡 Calling getProjectById with projectId:', projectId)

        if (isMounted) {
          setLoading(true)
          setError(null)
        }

        const project = await getProjectById(projectId)

        console.log('✅ Project fetched successfully:', project)

        if (isMounted) {
          setProjectData(project)
          setLoading(false)
        }
      } catch (err) {
        console.error('❌ Failed to fetch project:', err)

        if (isMounted) {
          setError(err.message || 'ไม่สามารถดึงข้อมูลโครงการได้')
          setLoading(false)
        }
      }
    }

    // เรียกใช้งาน async function เฉพาะเมื่อ dependencies พร้อม
    if (projectId && getProjectById) {
      fetchProjectData().catch(err => {
        console.error('❌ Unhandled error in fetchProjectData:', err)
      })
    }

    // Cleanup function ที่ชัดเจน
    return () => {
      isMounted = false
      console.log('🧹 ProjectTasksPage - useEffect cleanup executed')
    }
  }, [projectId, getProjectById])


  // ดึงข้อมูลผู้ใช้ (จำลอง - ควรเชื่อมต่อ API จริง)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // TODO: เชื่อมต่อกับ API จริง
        const mockUsers = [
          { user_id: 2, full_name: 'Admin User', username: 'admin' },
        ];
        setUsers(mockUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);
  // จัดการการเลือกงาน
  const handleTaskSelect = (task) => {
    setSelectedTask(task);
  };
  // เปิดฟอร์มเพิ่มงานใหม่
  const handleAddTask = () => {
    setSelectedTask(null);
    setIsEditing(false);
    setShowTaskForm(true);
  };
  // เปิดฟอร์มแก้ไขงาน
  const handleEditTask = (task) => {
    setSelectedTask(task);
    setIsEditing(true);
    setShowTaskForm(true);
  };
  // ปิดฟอร์ม
  const handleCloseForm = () => {
    setShowTaskForm(false);
    setSelectedTask(null);
    setIsEditing(false);
  };
  // บันทึกงานเสร็จแล้ว
  const handleTaskSave = () => {
    setShowTaskForm(false);
    setSelectedTask(null);
    setIsEditing(false);
    setRefreshKey(prev => prev + 1); // บังคับรีเฟรช TaskList
  };
  // ลบงาน
  const handleDeleteTask = async (task) => {
    Modal.confirm({
      title: `ลบงาน "${task.task_title}"`,
      content: 'คุณต้องการลบงานนี้ใช่หรือไม่?',
      okText: 'ลบ',
      cancelText: 'ยกเลิก',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await deleteTask(task.task_id);
          if (response.success) {
            message.success('ลบงานเรียบร้อยแล้ว');
            setRefreshKey(prev => prev + 1);
          } else {
            message.error(response.message || 'ไม่สามารถลบงานได้');
          }
        } catch (error) {
          console.error('Error deleting task:', error);
          message.error('เกิดข้อผิดพลาดในการลบงาน');
        }
      }
    });
  };
  // เปลี่ยนสถานะงาน
  const handleStatusChange = async (task, newStatus) => {
    try {
      const response = await updateTaskStatus(task.id, newStatus);
      if (response.success) {
        setRefreshKey(prev => prev + 1);
      } else {
        message.error(response.message || 'ไม่สามารถอัปเดตสถานะได้');
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      message.error('เกิดข้อผิดพลาดในการอัปเดตสถานะ');
    }
  };
  // กลับไปหน้ารายการโปรเจ็ค
  const handleBackToProjects = () => {
    navigate('/projects');
  };
  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Space size="large">
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            onClick={handleBackToProjects}
            size="large"
          >
            กลับไปโปรเจ็ค
          </Button>
          <div>
            <Title level={2} style={{ margin: 0, color: '#262626' }}>
              {projectData && (<>
                โปรเจ็ค ID: {`${projectId} : ${projectData?.project_name}`}
              </>)}
            </Title>

          </div>
        </Space>
        <Space size="middle">
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={() => setRefreshKey(prev => prev + 1)}
            size="large"
          >
            รีเฟรช
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddTask}
            size="large"
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
          >
            สร้างงานใหม่
          </Button>
        </Space>
      </Header>
      <Content style={{ padding: '24px' }}>
        {/* ปุ่มสร้างงานใหม่หลัก */}
        <Card
          style={{
            marginBottom: 24,
            background: 'linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%)',
            borderLeft: '4px solid #52c41a'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={3} style={{ marginBottom: 8, color: '#262626' }}>
                พร้อมที่จะสร้างงานใหม่แล้วใช่ไหม?
              </Title>
              <Text type="secondary">
                คลิกปุ่มด้านล่างเพื่อเริ่มสร้างงานใหม่สำหรับโปรเจ็คนี้
              </Text>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTask}
              size="large"
              style={{
                background: 'linear-gradient(45deg, #52c41a, #73d13d)',
                borderColor: '#52c41a',
                boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)',
                height: 'auto',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              สร้างงานใหม่
            </Button>
          </div>
        </Card>
        {/* Quick Actions */}
        <Card style={{ marginBottom: 24 }}>
          <Title level={4} style={{ marginBottom: 16 }}>การดำเนินการอย่างรวดเร็ว</Title>
          <Space wrap>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTask}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              สร้างงานใหม่
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => setRefreshKey(prev => prev + 1)}
            >
              อัปเดตรายการ
            </Button>
          </Space>
        </Card>
        {/* Task List */}
        <TaskList
          key={refreshKey}
          projectId={projectId}
          onTaskSelect={handleTaskSelect}
          onEdit={handleEditTask}
          onDelete={handleDeleteTask}
          onStatusChange={handleStatusChange}
        />
      </Content>
      {/* Task Form Modal */}
      <Modal
        title={isEditing ? 'แก้ไขงาน' : 'สร้างงานใหม่'}
        open={showTaskForm}
        onCancel={handleCloseForm}
        footer={null}
        width={800}
        destroyOnClose
      >
        <TaskForm
          projectId={projectId}
          task={selectedTask}
          isEditing={isEditing}
          users={users}
          onSave={handleTaskSave}
          onCancel={handleCloseForm}
        />
      </Modal>
      {/* Task Details Modal */}
      <Modal
        title="รายละเอียดงาน"
        open={selectedTask && !showTaskForm}
        onCancel={() => setSelectedTask(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedTask(null)}>
            ปิด
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEditTask(selectedTask)}
          >
            แก้ไข
          </Button>
        ]}
        width={600}
      >
        {selectedTask && (
          <div>
            <Title level={4}>{selectedTask.task_title}</Title>
            <Text>{selectedTask.task_description}</Text>
            <Descriptions
              column={2}
              bordered
              style={{ marginTop: 24 }}
              labelStyle={{ fontWeight: 'bold' }}
            >
              <Descriptions.Item label="สถานะ">
                {selectedTask.status === 'todo' ? 'รอดำเนินการ' :
                  selectedTask.status === 'in_progress' ? 'กำลังดำเนินการ' :
                    selectedTask.status === 'testing' ? 'ทดสอบ' :
                      selectedTask.status === 'done' ? 'เสร็จสิ้น' : selectedTask.status}
              </Descriptions.Item>
              <Descriptions.Item label="ลำดับความสำคัญ">
                {selectedTask.priority === 'low' ? 'ต่ำ' :
                  selectedTask.priority === 'medium' ? 'ปานกลาง' :
                    selectedTask.priority === 'high' ? 'สูง' :
                      selectedTask.priority === 'urgent' ? 'เร่งด่วน' : selectedTask.priority}
              </Descriptions.Item>
              <Descriptions.Item label="ความคืบหน้า">
                {selectedTask.progress_percentage}%
              </Descriptions.Item>
              <Descriptions.Item label="ผู้รับผิดชอบ">
                {selectedTask.assigned_to || 'ไม่ได้ระบุ'}
              </Descriptions.Item>
              {selectedTask.due_date && (
                <Descriptions.Item label="กำหนดส่ง">
                  {new Date(selectedTask.due_date).toLocaleDateString('th-TH')}
                </Descriptions.Item>
              )}
              {selectedTask.estimated_hours && (
                <Descriptions.Item label="ชั่วโมงประมาณการ">
                  {selectedTask.estimated_hours} ชั่วโมง
                </Descriptions.Item>
              )}
            </Descriptions>
            {selectedTask.task_notes && (
              <div style={{ marginTop: 16 }}>
                <Text strong>หมายเหตุ:</Text>
                <div style={{ marginTop: 8 }}>
                  <Text>{selectedTask.task_notes}</Text>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
};
export default TasksPage;